import { useState } from "react";
import { useCustomMutation, useTranslate } from "@refinedev/core";
import { Button } from "@fawkes/ui";

import type { CommTemplateDetailData } from "../commTypes";

// communicatie AI (Slice 7): the editor's AI-assistant side panel. It is a faithful port of the Jmix
// comm-editor AI column (setAiProposal / setAiCheck): rewrite presets (formeler/vriendelijker/korter/
// langer/spelling/B1) + a custom-prompt rewrite, a deterministic check, and suggest-fields. Each action
// posts the CURRENT editor body (or a selection) to the api and shows the returned PROPOSAL in a review
// state with Apply / Cancel — Apply is the ONLY thing that mutates the editor body; nothing is ever
// auto-applied. URLs are RELATIVE (the dataProvider prepends /api once).
//
// HARD invariant mirror: the server already guarantees placeholder preservation + sanitization + the
// AVG boundary (only {tokens} are sent). The panel additionally REFUSES to apply a proposal the server
// flagged as not placeholder-safe (placeholdersPreserved === false) — the Apply button is disabled and
// the reason is shown, so a corrupted-token proposal can never reach the document.

type Translate = ReturnType<typeof useTranslate>;

interface Proposal {
  proposalHtml: string;
  summary: string;
  warnings: string[];
  usedFields: string[];
  placeholdersPreserved: boolean;
  aiUsed: boolean;
}

interface Issue {
  severity: string;
  title: string;
  description: string;
  suggestedAction: string;
}
interface Suggestion {
  title: string;
  proposalHtml: string;
}
interface CheckResult {
  score: number;
  issues: Issue[];
  suggestions: Suggestion[];
  aiUsed: boolean;
}

const REWRITE_PRESETS: { action: string; key: string }[] = [
  { action: "friendlier", key: "communicatieAi.rewrite.friendlier" },
  { action: "formal", key: "communicatieAi.rewrite.formal" },
  { action: "shorter", key: "communicatieAi.rewrite.shorter" },
  { action: "longer", key: "communicatieAi.rewrite.longer" },
  { action: "grammar", key: "communicatieAi.rewrite.grammar" },
  { action: "b1", key: "communicatieAi.rewrite.b1" },
];

export function CommAiPanel({
  template,
  getBodyHtml,
  onApplyBody,
  onInsertToken,
  t,
  disabled,
}: {
  template: CommTemplateDetailData;
  /** Returns the editor's current full HTML (called lazily at action time, never resolves tokens). */
  getBodyHtml: () => string;
  /** Apply a reviewed proposal: swaps the editor body, preserving the variable pills. */
  onApplyBody: (html: string) => void;
  /** Insert a single {Group.Field} token (used by suggest-fields suggestions). */
  onInsertToken: (token: string) => void;
  t: Translate;
  disabled?: boolean;
}) {
  const { mutate, isLoading } = useCustomMutation();
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [check, setCheck] = useState<CheckResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [customPrompt, setCustomPrompt] = useState("");
  const [showCustom, setShowCustom] = useState(false);

  const busy = isLoading || disabled;

  function clear() {
    setProposal(null);
    setCheck(null);
    setError(null);
  }

  function rewrite(action: string, customInstruction?: string) {
    clear();
    mutate(
      {
        url: "communicatie/templates/ai/rewrite",
        method: "post",
        values: {
          templateId: template.id,
          action,
          fullTemplateHtml: getBodyHtml(),
          language: template.languageCode,
          templateType: template.type,
          category: template.category,
          customInstruction: customInstruction ?? null,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setProposal(res.data as unknown as Proposal),
        onError: (e) => setError(e?.message ?? t("communicatieAi.error.generic")),
      },
    );
  }

  function runCheck() {
    clear();
    mutate(
      {
        url: "communicatie/templates/ai/check",
        method: "post",
        values: {
          templateId: template.id,
          bodyHtml: getBodyHtml(),
          subject: template.subject,
          templateType: template.type,
          category: template.category,
          requiredBlockEnabled: template.requiredBlockEnabled,
          requiredBlockText: template.requiredBlockText,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setCheck(res.data as unknown as CheckResult),
        onError: (e) => setError(e?.message ?? t("communicatieAi.error.generic")),
      },
    );
  }

  function suggestFields() {
    clear();
    mutate(
      {
        url: "communicatie/templates/ai/suggest-fields",
        method: "post",
        values: {
          templateId: template.id,
          bodyHtml: getBodyHtml(),
          subject: template.subject,
          templateType: template.type,
          category: template.category,
        },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: (res) => setCheck(res.data as unknown as CheckResult),
        onError: (e) => setError(e?.message ?? t("communicatieAi.error.generic")),
      },
    );
  }

  function applyProposal() {
    if (!proposal || !proposal.placeholdersPreserved) return;
    onApplyBody(proposal.proposalHtml);
    clear();
  }

  /** Pull a single {Token} out of a "<span>{Group.Field}</span>" suggestion proposal, if it is one. */
  function tokenOf(suggestion: Suggestion): string | null {
    const m = suggestion.proposalHtml.match(/^\s*<span>\{([A-Za-z0-9_.]+)\}<\/span>\s*$/);
    return m ? m[1] : null;
  }

  return (
    <aside className="comm-ai-panel" aria-label={t("communicatieAi.title")}>

      {/* ── Actions (hidden while reviewing a proposal/check) ───────────────────────── */}
      {!proposal && !check && (
        <div className="comm-ai-panel__actions">
          <div className="comm-ai-panel__group-label">{t("communicatieAi.rewrite.label")}</div>
          {REWRITE_PRESETS.map((p) => (
            <button
              key={p.action}
              type="button"
              className="comm-ai-panel__action"
              disabled={busy}
              onClick={() => rewrite(p.action)}
            >
              {t(p.key)}
            </button>
          ))}

          {!showCustom ? (
            <button
              type="button"
              className="comm-ai-panel__action"
              disabled={busy}
              onClick={() => setShowCustom(true)}
            >
              {t("communicatieAi.rewrite.custom")}
            </button>
          ) : (
            <div className="comm-ai-panel__custom">
              <textarea
                className="comm-ai-panel__custom-input"
                rows={3}
                value={customPrompt}
                placeholder={t("communicatieAi.rewrite.customPlaceholder")}
                onChange={(e) => setCustomPrompt(e.target.value)}
                aria-label={t("communicatieAi.rewrite.customPlaceholder")}
              />
              <div className="comm-ai-panel__custom-actions">
                <Button
                  variant="primary"
                  onClick={() => rewrite("custom", customPrompt)}
                  disabled={busy || customPrompt.trim().length === 0}
                >
                  {t("communicatieAi.rewrite.customRun")}
                </Button>
                <Button variant="secondary" onClick={() => setShowCustom(false)} disabled={busy}>
                  {t("common.cancel")}
                </Button>
              </div>
            </div>
          )}

          <div className="comm-ai-panel__group-label">{t("communicatieAi.review.label")}</div>
          <button type="button" className="comm-ai-panel__action" disabled={busy} onClick={runCheck}>
            {t("communicatieAi.action.check")}
          </button>
          <button type="button" className="comm-ai-panel__action" disabled={busy} onClick={suggestFields}>
            {t("communicatieAi.action.suggestFields")}
          </button>
        </div>
      )}

      {busy && <p className="comm-ai-panel__busy">{t("communicatieAi.busy")}</p>}
      {error && (
        <div className="comm-ai-panel__alert comm-ai-panel__alert--error" role="alert">
          {error}
        </div>
      )}

      {/* ── Proposal review (Apply / Cancel) ───────────────────────────────────────── */}
      {proposal && (
        <div className="comm-ai-panel__proposal">
          <div className="comm-ai-panel__proposal-head">{t("communicatieAi.proposal.title")}</div>
          {proposal.summary && <p className="comm-ai-panel__summary">{proposal.summary}</p>}
          {!proposal.aiUsed && (
            <p className="comm-ai-panel__hint">{t("communicatieAi.proposal.noProvider")}</p>
          )}

          <div
            className="comm-ai-panel__preview"
            // The proposal HTML is already server-sanitized (scripts stripped); this preview is read-only.
            dangerouslySetInnerHTML={{ __html: proposal.proposalHtml }}
          />

          {!proposal.placeholdersPreserved && (
            <div className="comm-ai-panel__alert comm-ai-panel__alert--warn" role="alert">
              {t("communicatieAi.proposal.notApplicable")}
            </div>
          )}
          {proposal.warnings.length > 0 && (
            <ul className="comm-ai-panel__warnings">
              {proposal.warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          )}

          <div className="comm-ai-panel__proposal-actions">
            <Button
              variant="primary"
              onClick={applyProposal}
              disabled={busy || !proposal.placeholdersPreserved}
              title={!proposal.placeholdersPreserved ? t("communicatieAi.proposal.notApplicable") : undefined}
            >
              {t("communicatieAi.proposal.apply")}
            </Button>
            <Button variant="secondary" onClick={clear} disabled={busy}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      )}

      {/* ── Check / suggest-fields result ──────────────────────────────────────────── */}
      {check && (
        <div className="comm-ai-panel__check">
          <div className="comm-ai-panel__proposal-head">
            {check.score >= 0
              ? t("communicatieAi.check.title", { args: [check.score] })
              : t("communicatieAi.suggestFields.title")}
          </div>

          {check.issues.length > 0 && (
            <ul className="comm-ai-panel__issues">
              {check.issues.map((iss, i) => (
                <li
                  key={i}
                  className={`comm-ai-panel__issue comm-ai-panel__issue--${iss.severity}`}
                >
                  <strong>{iss.title}</strong>
                  <span>{iss.description}</span>
                  {iss.suggestedAction && (
                    <span className="comm-ai-panel__issue-action">{iss.suggestedAction}</span>
                  )}
                </li>
              ))}
            </ul>
          )}

          {check.suggestions.length > 0 && (
            <ul className="comm-ai-panel__suggestions">
              {check.suggestions.map((sg, i) => {
                const token = tokenOf(sg);
                return (
                  <li key={i} className="comm-ai-panel__suggestion">
                    <span>{sg.title}</span>
                    {token && (
                      <button
                        type="button"
                        className="comm-ai-panel__suggestion-insert"
                        disabled={busy}
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => onInsertToken(token)}
                      >
                        {t("communicatieAi.suggestFields.insert")}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {check.issues.length === 0 && check.suggestions.length === 0 && (
            <p className="comm-ai-panel__hint">{t("communicatieAi.check.allGood")}</p>
          )}

          <div className="comm-ai-panel__proposal-actions">
            <Button variant="secondary" onClick={clear} disabled={busy}>
              {t("communicatieAi.action.close")}
            </Button>
          </div>
        </div>
      )}
    </aside>
  );
}
